'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { runOnFirstInteractionOrTimeout } from '@/lib/deferred-script';

const HUBSPOT_PORTAL_ID = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID || '5417380';

function shouldLoadHubSpotTracking(pathname: string): boolean {
  return !pathname.startsWith('/admin');
}

/**
 * Deferred HubSpot tracking loader (5417380.js). Skipped on /admin/* so
 * marketing popups and web interactives do not overlay the admin dashboard.
 *
 * PERF CONTRACT: the hs-scripts loader pulls the entire HubSpot suite
 * (analytics + conversations chat widget ~370KB + forms embed ~180KB +
 * banner + web interactives). It must stay behind
 * runOnFirstInteractionOrTimeout (first interaction or 10s fallback) so it
 * never lands inside the Lighthouse trace — see src/lib/deferred-script.ts
 * and tests/unit/perf/perf-guards.test.ts before changing this.
 */
export function HubSpotTrackingScript(): null {
  const pathname = usePathname();

  useEffect(() => {
    if (!shouldLoadHubSpotTracking(pathname)) {
      return;
    }

    return runOnFirstInteractionOrTimeout(() => {
      const src = `https://js.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;
      if (document.querySelector(`script[src="${src}"]`)) {
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    });
  }, [pathname]);

  return null;
}
